import React from 'react';
import { NavLink } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { Gallery } from '../components/Gallery';
import {
  COMPANY_INFO,
  TECHNICAL_SPECS,
  SITE_IMAGES,
  getCdnImage,
} from '../data/content';

export function System3E() {
  return (
    <>
      <SEO
        title="SYSTEM 3E – Produkt & Tekniske Specifikationer"
        description="Læs alt om SYSTEM 3E perlitelementerne. Unik Click-Click samling, U-værdi på 0,198 W/m²K og REI 240 + M brandklasse. AGT Nordic Group ApS er eneforhandler i Danmark."
      />

      {/* Hero */}
      <section className="bg-[#243447] text-white py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={getCdnImage(SITE_IMAGES.elementsCloseUp, 1600, 80)}
            alt="SYSTEM 3E elementer close up"
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-[#162332]/45" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#819FA8] font-extrabold text-xs uppercase tracking-widest block mb-2">
              Produkt- & Teknologipræsentation
            </span>
            <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight mb-6">
              SYSTEM 3E
            </h1>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
              Fremtidens modulære byggesystem baseret på det naturlige mineral perlit. Kombinerer bærende styrke og ekstraordinær isoleringsevne i én præcis Click-Click konstruktion.
            </p>
          </div>
        </div>
      </section>

      {/* Main Product Explanation */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mb-6">
                En ny standard for råhuse og vægkonstruktion
              </h2>
              <p className="text-slate-700 text-base leading-relaxed mb-4">
                SYSTEM 3E er udviklet som et højteknologisk svar på den tunge og tidskrævende murerproces. Elementerne fremstilles med ekstrem geometrisk præcision af perlit – et opskummet vulkansk mineral.
              </p>
              <p className="text-slate-600 text-sm leading-relaxed mb-6">
                Resultatet er et let, uforgængeligt og formstabilt materiale. Da elementerne låser mekanisk i hinanden via den karakteristiske <strong>Click-Click samling</strong>, udelades mørtel, lim og vand fuldstændigt under opbygningen.
              </p>

              <div className="bg-slate-50 border-l-4 border-red-600 p-4 rounded-r-xl mb-6">
                <p className="text-slate-900 font-bold text-sm">
                  Kombination af struktur & isolering
                </p>
                <p className="text-slate-600 text-xs mt-1">
                  I modsætning til traditionelt murværk med separat isolering og hulmur, udgør 3E-elementet både den bærende struktur og isoleringslaget på én gang.
                </p>
              </div>

              <div className="flex flex-wrap gap-4 text-xs font-semibold text-slate-700">
                <span className="bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200">
                  <i className="fa-solid fa-droplet-slash text-red-600 mr-1.5" aria-hidden="true" />
                  Ingen mørtel eller lim
                </span>
                <span className="bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200">
                  <i className="fa-solid fa-temperature-arrow-down text-red-600 mr-1.5" aria-hidden="true" />
                  Høj termisk isolering
                </span>
                <span className="bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200">
                  <i className="fa-solid fa-fire-shield text-red-600 mr-1.5" aria-hidden="true" />
                  Ubændbart perlit
                </span>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden bg-transparent">
              <img
                src={getCdnImage(SITE_IMAGES.perliteDetail, 1000, 85)}
                alt="Perlit mineral struktur i SYSTEM 3E"
                className="w-full h-[400px] object-contain"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-slate-950/90 text-white p-4 rounded-xl border border-slate-800 text-xs">
                <span className="text-red-400 font-bold uppercase tracking-wider block mb-1">
                  Materiale: Perlit
                </span>
                <p className="text-slate-300">
                  Perlit fremstilles af naturlig vulkansk sten, der varmebehandles til en let, ekstremt isolerende og uforgængelig mineralstruktur.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specs Table Component */}
      <section className="py-16 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-red-600 font-extrabold text-xs uppercase tracking-widest block mb-2">
              Dokumenterede Værdier
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Tekniske Specifikationer
            </h2>
            <p className="text-slate-600 text-sm mt-2">
              Alle tal og værdier er baseret på de officielle tekniske godkendelser og testrapporter fra producenten.
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden max-w-4xl mx-auto">
            <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
              <span className="font-bold text-sm tracking-wide">SYSTEM 3E Basiselement</span>
              <span className="text-xs text-red-400 font-semibold uppercase">
                {COMPANY_INFO.soleDistributorText}
              </span>
            </div>

            <div className="divide-y divide-slate-100">
              {TECHNICAL_SPECS.map((spec, idx) => (
                <div
                  key={idx}
                  className="px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 hover:bg-slate-50/50 transition-colors"
                >
                  <div className="sm:w-1/2">
                    <span className="font-bold text-slate-900 text-sm">{spec.label}</span>
                    <p className="text-slate-500 text-xs">{spec.note}</p>
                  </div>
                  <div className="sm:w-1/2 sm:text-right">
                    <span className="inline-block bg-slate-100 text-slate-900 font-extrabold text-sm px-3 py-1 rounded-md border border-slate-200">
                      {spec.value}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>



      {/* Bottom Contact Banner */}
      <section className="py-12 bg-slate-900 text-white border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl font-bold text-white">Har du tekniske spørgsmål til systemet?</h3>
            <p className="text-slate-400 text-sm mt-1">
              Kontakt AGT Nordic Group ApS for tekniske datablad, tegninger eller projektberegning.
            </p>
          </div>
          <NavLink
            to="/kontakt"
            className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-3 rounded-lg text-sm transition-all whitespace-nowrap"
          >
            Kontakt vores rådgivere
          </NavLink>
        </div>
      </section>
    </>
  );
}
